import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';

// --- Animations ---
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.02); }
  100% { transform: scale(1); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

// --- Styled Components ---

// Dark overlay for center/welcome step
const DarkOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.75);
  z-index: 2999;
  pointer-events: none;
`;

// Spotlight effect using a massive box-shadow - MUST BE FIXED POSITION
const Spotlight = styled.div`
  position: fixed;
  border-radius: 12px;
  pointer-events: none;
  transition: all 0.3s ease-out;
  z-index: 3000;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.75); 
  background: transparent;
  
  /* Add a subtle glow/border to the highlight itself */
  &::after {
    content: '';
    position: absolute;
    top: -3px;
    left: -3px;
    right: -3px;
    bottom: -3px;
    border: 3px solid #1CA8CB;
    border-radius: 15px;
    animation: ${pulse} 2s infinite ease-in-out;
  }
`;

// Responsive tooltip - uses $isCenter prop to handle center positioning
const Tooltip = styled.div`
  position: fixed;
  background: white;
  padding: 20px;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  width: 320px;
  max-width: calc(100vw - 32px);
  pointer-events: auto;
  z-index: 3001;
  animation: ${fadeIn} 0.3s ease-out;

  @media (max-width: 768px) {
    width: ${props => props.$isCenter ? '280px' : 'auto'};
    left: ${props => props.$isCenter ? '50%' : '16px'} !important;
    right: ${props => props.$isCenter ? 'auto' : '16px'} !important;
    transform: ${props => props.$isCenter ? 'translate(-50%, -50%)' : 'none'};
    padding: 16px;
    max-width: ${props => props.$isCenter ? 'calc(100vw - 32px)' : 'none'};
  }
`;

const Title = styled.h3`
  margin: 0 0 8px 0;
  color: #004e61;
  font-family: 'Merriweather', serif;
  font-size: 16px;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const Text = styled.p`
  margin: 0 0 16px 0;
  color: #555;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 12px;
    margin-bottom: 12px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const Button = styled.button`
  background: ${props => props.$primary ? 'linear-gradient(135deg, #1CA8CB 0%, #008ba3 100%)' : 'transparent'};
  color: ${props => props.$primary ? 'white' : '#666'};
  border: ${props => props.$primary ? 'none' : '1px solid transparent'};
  padding: 8px 16px;
  border-radius: 50px;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-1px);
    color: ${props => props.$primary ? 'white' : '#1CA8CB'};
  }

  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 11px;
  }
`;

const StepIndicator = styled.span`
  font-size: 11px;
  color: #999;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
`;

const STORAGE_KEY = 'trippro_tour_completed';

// Check if tour was already completed - synchronous check
const isTourCompleted = () => {
    try {
        return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
};

// Map modal steps to tour step indices
const getMinTourStepForModalStep = (modalStep) => {
    switch (modalStep) {
        case 1: return 2;
        case 2: return 4;
        case 3: return 6;
        case 4: return 7;
        default: return 2;
    }
};

// Check if mobile
const isMobile = () => window.innerWidth <= 768;

const Walkthrough = ({ isModalOpen, modalStep = 1 }) => {
    const [active, setActive] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);
    const [coords, setCoords] = useState(null);

    // Check localStorage on mount - only show tour for new users
    useEffect(() => {
        if (isTourCompleted()) {
            setActive(false);
            return;
        }
        const timer = setTimeout(() => {
            if (!isTourCompleted()) {
                setActive(true);
            }
        }, 1500);
        return () => clearTimeout(timer);
    }, []);

    const finishTour = useCallback(() => {
        try {
            localStorage.setItem(STORAGE_KEY, 'true');
        } catch (e) {
            console.error('Failed to save tour completion status:', e);
        }
        setActive(false);
    }, []);

    // Memoize steps to prevent infinite loops
    const steps = useMemo(() => [
        {
            target: null,
            title: "Welcome to TripPro AI",
            content: "Let us help you plan your dream trip in seconds with our AI-powered planner.",
            placement: "center"
        },
        {
            target: "#tour-ai-button",
            title: "Start Planning",
            content: "Click this button to launch the AI Trip Planner wizard.",
            placement: "left"
        },
        {
            target: "#tour-step1-content",
            title: "Choose Destination",
            content: "First, tell us which state you'd like to explore.",
            placement: "bottom",
            modalStep: 1
        },
        {
            target: "#tour-step1-next",
            title: "Next Step",
            content: "Once you've picked a state, click Next.",
            placement: "top",
            modalStep: 1
        },
        {
            target: "#tour-date-picker",
            title: "Pick Your Dates",
            content: "Select your travel dates from the calendar.",
            placement: "bottom",
            modalStep: 2
        },
        {
            target: "#tour-step2-next",
            title: "Confirm Dates",
            content: "Click Next to customize your itinerary.",
            placement: "top",
            modalStep: 2
        },
        {
            target: "#tour-trip-details",
            title: "Customize Your Trip",
            content: "Select destinations, activities, and preferences.",
            placement: "bottom",
            modalStep: 3
        },
        {
            target: "#tour-generate-button",
            title: "Generate Itinerary",
            content: "Click to let our AI build your travel plan!",
            placement: "top",
            modalStep: 4
        },
        {
            target: null,
            title: "Generating Your Itinerary! 🎉",
            content: "Your personalized travel plan is being created. Click Done to complete the tour.",
            placement: "center",
            isFinalStep: true
        }
    ], []);

    // Sync tour step with modal step when modal step changes
    useEffect(() => {
        if (!isModalOpen || !active) return;

        const minTourStep = getMinTourStepForModalStep(modalStep);

        // If current tour step is behind the modal step, catch up
        if (stepIndex >= 2 && stepIndex < minTourStep) {
            setStepIndex(minTourStep);
        }
    }, [modalStep, isModalOpen, active, stepIndex]);

    // Track if user just clicked Next to go to step 1 (to prevent immediate skip)
    const [justAdvancedToStep1, setJustAdvancedToStep1] = useState(false);

    // Auto-advance when modal opens from step 1, but only if user didn't just click Next
    useEffect(() => {
        if (isModalOpen && stepIndex === 1 && !justAdvancedToStep1) {
            setStepIndex(2);
        }
        // Reset the flag after a short delay to allow normal behavior
        if (justAdvancedToStep1) {
            const timer = setTimeout(() => setJustAdvancedToStep1(false), 100);
            return () => clearTimeout(timer);
        }
    }, [isModalOpen, stepIndex, justAdvancedToStep1]);

    // Update spotlight coordinates
    const updatePosition = useCallback(() => {
        if (!active) return;

        const currentStep = steps[stepIndex];

        if (currentStep.placement === "center" || !currentStep.target) {
            setCoords(prev => {
                if (prev?.isCenter) return prev;
                return { isCenter: true };
            });
            return;
        }

        const element = document.querySelector(currentStep.target);
        if (element) {
            const rect = element.getBoundingClientRect();
            const padding = 8;

            const newCoords = {
                top: rect.top - padding,
                left: rect.left - padding,
                width: rect.width + (padding * 2),
                height: rect.height + (padding * 2),
                isCenter: false
            };

            // Only update if changed to prevent re-renders
            setCoords(prev => {
                if (prev &&
                    prev.top === newCoords.top &&
                    prev.left === newCoords.left &&
                    prev.width === newCoords.width &&
                    prev.height === newCoords.height) {
                    return prev;
                }
                return newCoords;
            });
        } else {
            setCoords(prev => prev === null ? null : null);
        }
    }, [stepIndex, active, steps]);

    useEffect(() => {
        updatePosition();
        const interval = setInterval(updatePosition, 200);
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            clearInterval(interval);
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [updatePosition]);

    // Listen for Generate button click to advance to final "Generating" step
    useEffect(() => {
        if (!active) return;

        const generateButton = document.querySelector('#tour-generate-button');
        if (!generateButton) return;

        const handleGenerateClick = () => {
            // Move to the final "Generating" step
            setStepIndex(steps.length - 1);
        };

        generateButton.addEventListener('click', handleGenerateClick);
        return () => {
            generateButton.removeEventListener('click', handleGenerateClick);
        };
    }, [active, steps.length]);

    const handleNext = useCallback(() => {
        if (stepIndex < steps.length - 1) {
            // Set flag when advancing from step 0 to step 1 to prevent immediate skip
            if (stepIndex === 0) {
                setJustAdvancedToStep1(true);
            }
            setStepIndex(prev => prev + 1);
        } else {
            finishTour();
        }
    }, [stepIndex, steps.length, finishTour]);

    const handleSkip = useCallback(() => {
        finishTour();
    }, [finishTour]);

    const handleBack = useCallback(() => {
        if (stepIndex > 0) setStepIndex(prev => prev - 1);
    }, [stepIndex]);

    if (!active) return null;

    const currentStep = steps[stepIndex];

    // Safety: If step expects a specific modal step but we're not there, hide
    if (currentStep.modalStep && isModalOpen && currentStep.modalStep !== modalStep) {
        return null;
    }

    // Safety: If step expects modal but modal is closed
    if (currentStep.modalStep && !isModalOpen) {
        return null;
    }

    // Calculate Tooltip Position - responsive
    let tooltipStyle = {};
    const isCenter = coords?.isCenter;
    const mobile = isMobile();

    if (isCenter) {
        tooltipStyle = {
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)'
        };
    } else if (coords) {
        const gap = 15;

        if (mobile) {
            // On mobile, position tooltip at top for modal steps, or dynamically otherwise
            const isModalStep = currentStep.modalStep !== undefined;

            if (isModalStep) {
                // For modal steps, always place at top to avoid overlapping modal content
                tooltipStyle = {
                    top: 80 // Below the modal close button area
                };
            } else {
                // For non-modal steps (like AI button), position relative to element
                const topSpace = coords.top;
                const bottomSpace = window.innerHeight - (coords.top + coords.height);

                if (topSpace > 180) {
                    tooltipStyle = {
                        top: Math.max(20, coords.top - 160)
                    };
                } else if (bottomSpace > 200) {
                    tooltipStyle = {
                        top: Math.min(coords.top + coords.height + 15, window.innerHeight - 200)
                    };
                } else {
                    tooltipStyle = {
                        top: 100
                    };
                }
            }
        } else {
            // Desktop positioning
            switch (currentStep.placement) {
                case 'left':
                    tooltipStyle = {
                        top: Math.max(20, coords.top + (coords.height / 2) - 80),
                        left: Math.max(20, coords.left - 340 - gap)
                    };
                    break;
                case 'right':
                    tooltipStyle = {
                        top: Math.max(20, coords.top),
                        left: Math.min(window.innerWidth - 340, coords.left + coords.width + gap)
                    };
                    break;
                case 'top':
                    tooltipStyle = {
                        top: Math.max(20, coords.top - 150 - gap),
                        left: Math.max(20, Math.min(window.innerWidth - 340, coords.left + (coords.width / 2) - 160))
                    };
                    break;
                default: // bottom
                    tooltipStyle = {
                        top: Math.min(window.innerHeight - 180, coords.top + coords.height + gap),
                        left: Math.max(20, Math.min(window.innerWidth - 340, coords.left + (coords.width / 2) - 160))
                    };
            }
        }
    }

    // Don't render if we don't have valid coordinates yet (except for center step)
    if (!coords && !isCenter) return null;

    return (
        <>
            {/* For center step, use full overlay; otherwise use spotlight */}
            {isCenter ? (
                <DarkOverlay />
            ) : coords && (
                <Spotlight
                    style={{
                        top: coords.top,
                        left: coords.left,
                        width: coords.width,
                        height: coords.height
                    }}
                />
            )}
            <Tooltip style={tooltipStyle} $isCenter={isCenter}>
                <Title>{currentStep.title}</Title>
                <Text>{currentStep.content}</Text>
                <ButtonGroup>
                    <Button onClick={handleSkip}>Skip Tour</Button>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <StepIndicator>{stepIndex + 1}/{steps.length}</StepIndicator>
                        {stepIndex > 0 && <Button onClick={handleBack}>Back</Button>}
                        <Button $primary onClick={handleNext}>
                            {stepIndex === steps.length - 1 ? "Done" : "Next"}
                        </Button>
                    </div>
                </ButtonGroup>
            </Tooltip>
        </>
    );
};

export default Walkthrough;
